<template>
  <section class="d-grid gap-4">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h4 mb-1">Imágenes de catálogo</h2>
        <p class="text-muted mb-0">
          Backfill automatizado de imágenes de producto (go-upc → consulta externa por código de barras → búsqueda web con IA).
        </p>
      </div>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'MaintenanceHome' }">
        Volver a Mantenimiento
      </router-link>
    </header>

    <div v-if="!canManageImages" class="alert alert-warning mb-0">
      Esta sección está disponible sólo para usuarios con el permiso <code>editar_productos</code>.
    </div>

    <template v-else>
      <div class="card shadow-sm">
        <div class="card-body">
          <h3 class="h6 mb-3">Auditoría de imágenes</h3>
          <div v-if="auditLoading" class="text-muted">Calculando...</div>
          <div v-else-if="auditError" class="text-danger">{{ auditError }}</div>
          <div v-else class="row g-3">
            <div class="col-6 col-md-3">
              <div class="audit-stat">
                <span class="audit-stat-value">{{ audit.total }}</span>
                <span class="audit-stat-label">Total productos</span>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="audit-stat audit-stat--warn">
                <span class="audit-stat-value">{{ audit.nula }}</span>
                <span class="audit-stat-label">Sin imagen (nula)</span>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="audit-stat audit-stat--warn">
                <span class="audit-stat-value">{{ audit.invalida_conocida }}</span>
                <span class="audit-stat-label">Con el patrón antiguo (código de barras como imagen)</span>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="audit-stat audit-stat--ok">
                <span class="audit-stat-value">{{ audit.revisar }}</span>
                <span class="audit-stat-label">Con URL propia (no verificada individualmente)</span>
              </div>
            </div>
          </div>
          <p class="text-muted small mt-2 mb-0">
            "Con URL propia" no garantiza que la imagen cargue — la auditoría es solo por SQL,
            sin revisar cada link uno por uno (ver PLAN). El backfill entra a "nula" y al patrón antiguo.
          </p>
          <button class="btn btn-outline-secondary btn-sm mt-3" :disabled="auditLoading" @click="fetchAudit">
            Actualizar auditoría
          </button>
        </div>
      </div>

      <div class="card shadow-sm">
        <div class="card-body">
          <h3 class="h6 mb-3">Backfill automatizado</h3>

          <div class="row g-3 align-items-end mb-3">
            <div class="col-auto">
              <label class="form-label">Tamaño del lote</label>
              <input
                v-model.number="batchSize"
                type="number"
                min="1"
                max="500"
                class="form-control"
                style="width: 140px"
                :disabled="isRunning"
              />
            </div>
            <div class="col-auto">
              <button class="btn btn-dark" :disabled="isRunning || starting" @click="runBackfill">
                <span v-if="isRunning || starting" class="spinner-border spinner-border-sm me-2"></span>
                {{ isRunning ? 'Corriendo...' : 'Ejecutar backfill' }}
              </button>
            </div>
            <div class="col-auto" v-if="isRunning || isCancelando">
              <button class="btn btn-outline-danger" :disabled="isCancelando || cancelling" @click="cancelBackfill">
                <span v-if="cancelling" class="spinner-border spinner-border-sm me-2"></span>
                {{ isCancelando ? 'Cancelando...' : 'Cancelar' }}
              </button>
            </div>
          </div>

          <div v-if="runError" class="alert alert-danger py-2">{{ runError }}</div>

          <div v-if="status.iniciado_en" class="backfill-status">
            <div class="d-flex justify-content-between mb-1">
              <span>{{ status.procesados }} / {{ status.total }} procesados</span>
              <span class="badge" :class="estadoBadgeClass">
                {{ estadoLabel }}
              </span>
            </div>
            <div class="progress mb-3" style="height: 8px" v-if="status.total">
              <div
                class="progress-bar"
                :style="{ width: progressPercent + '%' }"
              ></div>
            </div>
            <div class="row g-3">
              <div class="col-6 col-md-3">
                <div class="audit-stat">
                  <span class="audit-stat-value">{{ status.resueltos_por_fuente.go_upc }}</span>
                  <span class="audit-stat-label">go-upc</span>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="audit-stat">
                  <span class="audit-stat-value">{{ status.resueltos_por_fuente.consulta_externa_codigo_barras }}</span>
                  <span class="audit-stat-label">Consulta externa</span>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="audit-stat">
                  <span class="audit-stat-value">{{ status.resueltos_por_fuente.ia_web }}</span>
                  <span class="audit-stat-label">Búsqueda web IA</span>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="audit-stat audit-stat--warn">
                  <span class="audit-stat-value">{{ status.pendientes }}</span>
                  <span class="audit-stat-label">Pendientes (sin imagen)</span>
                </div>
              </div>
            </div>
            <div v-if="status.errores" class="text-danger small mt-2">
              {{ status.errores }} producto(s) con error durante el procesamiento (ver logs del servidor).
            </div>

            <div v-if="status.detalle && status.detalle.length" class="mt-3">
              <h4 class="h6 mb-2">Detalle por producto ({{ status.detalle.length }})</h4>
              <div class="table-responsive backfill-detalle">
                <table class="table table-sm table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Resultado</th>
                      <th>Fuente</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="fila in detalleOrdenado" :key="fila.producto_id">
                      <td>{{ fila.nombre || `#${fila.producto_id}` }}</td>
                      <td>
                        <span class="badge" :class="detalleBadgeClass(fila.resultado)">
                          {{ detalleLabel(fila.resultado) }}
                        </span>
                      </td>
                      <td class="text-muted small">{{ fila.fuente || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div v-else class="text-muted">
            Todavía no se ejecutó ningún backfill en esta sesión del servidor.
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { hasPermission } from '@/services/authSessionService'
import imageBackfillService from '@/services/imageBackfillService'

const canManageImages = computed(() => hasPermission('editar_productos'))

const audit = ref({ nula: 0, invalida_conocida: 0, revisar: 0, total: 0 })
const auditLoading = ref(false)
const auditError = ref('')

const batchSize = ref(50)
const starting = ref(false)
const cancelling = ref(false)
const runError = ref('')
const status = ref({
  status: 'idle',
  total: 0,
  procesados: 0,
  resueltos_por_fuente: { go_upc: 0, consulta_externa_codigo_barras: 0, ia_web: 0 },
  pendientes: 0,
  errores: 0,
  iniciado_en: null,
  terminado_en: null,
  detalle: []
})

let pollingTimer = null

const isRunning = computed(() => status.value.status === 'running')
const isCancelando = computed(() => status.value.status === 'cancelando')
const progressPercent = computed(() => {
  if (!status.value.total) return 0
  return Math.min(100, Math.round((status.value.procesados / status.value.total) * 100))
})

const ESTADOS = {
  running: { label: 'En curso', badge: 'text-bg-warning' },
  cancelando: { label: 'Cancelando...', badge: 'text-bg-warning' },
  cancelado: { label: 'Cancelado', badge: 'text-bg-secondary' },
}
const estadoLabel = computed(() => ESTADOS[status.value.status]?.label || 'Finalizado')
const estadoBadgeClass = computed(() => ESTADOS[status.value.status]?.badge || 'text-bg-success')

// Más reciente primero: es lo que más le interesa ver a quien está
// mirando un lote largo en curso.
const detalleOrdenado = computed(() => [...(status.value.detalle || [])].reverse())

const DETALLE = {
  resuelto: { label: 'Resuelto', badge: 'text-bg-success' },
  sin_imagen: { label: 'Sin imagen', badge: 'text-bg-warning' },
  error: { label: 'Error', badge: 'text-bg-danger' },
}
const detalleLabel = (resultado) => DETALLE[resultado]?.label || resultado
const detalleBadgeClass = (resultado) => DETALLE[resultado]?.badge || 'text-bg-light'

async function fetchAudit() {
  auditLoading.value = true
  auditError.value = ''
  try {
    audit.value = await imageBackfillService.getAudit()
  } catch (e) {
    auditError.value = 'No se pudo calcular la auditoría de imágenes'
  } finally {
    auditLoading.value = false
  }
}

async function fetchStatus() {
  try {
    status.value = await imageBackfillService.getStatus()
    if (status.value.status === 'running' || status.value.status === 'cancelando') {
      schedulePoll()
    } else {
      stopPolling()
    }
  } catch (e) {
    stopPolling()
  }
}

function schedulePoll() {
  stopPolling()
  pollingTimer = setTimeout(fetchStatus, 2500)
}

function stopPolling() {
  if (pollingTimer) {
    clearTimeout(pollingTimer)
    pollingTimer = null
  }
}

async function runBackfill() {
  starting.value = true
  runError.value = ''
  try {
    status.value = await imageBackfillService.runBatch(batchSize.value)
    schedulePoll()
    // Refresca la auditoría cuando termine, no en cada tick de polling.
  } catch (e) {
    if (e?.response?.status === 409) {
      runError.value = 'Ya hay un backfill en ejecución.'
      fetchStatus()
    } else {
      runError.value = 'No se pudo iniciar el backfill.'
    }
  } finally {
    starting.value = false
  }
}

async function cancelBackfill() {
  cancelling.value = true
  runError.value = ''
  try {
    status.value = await imageBackfillService.cancelBatch()
    schedulePoll()
  } catch (e) {
    runError.value = 'No se pudo cancelar el backfill.'
  } finally {
    cancelling.value = false
  }
}

onMounted(() => {
  if (!canManageImages.value) return
  fetchAudit()
  fetchStatus()
})

onBeforeUnmount(() => {
  stopPolling()
})
</script>

<style scoped>
.backfill-detalle {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}

.audit-stat {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.65rem 0.85rem;
  border-radius: 0.6rem;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
}

.audit-stat--warn {
  background: #fffbeb;
  border-color: #fde68a;
}

.audit-stat--ok {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.audit-stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
}

.audit-stat-label {
  font-size: 0.75rem;
  color: #64748b;
}

.progress-bar {
  background-color: #111827;
}
</style>
